import { Grid } from '@mantine/core';
import type { ComputeCostMetrics } from '@repo/api-codegen';
import { memo, useCallback } from 'react';
import type { ComputeCostMetricCardType } from '../../types';
import { MetricsOverviewCard } from '../MetricsOverviewCard';

export interface ComputeCostMetricCardsProps {
	computeCostMetrics?: ComputeCostMetrics;
	onMetricCardClick?: (metricType: ComputeCostMetricCardType) => void;
}

export const ComputeCostMetricCards = memo(
	({ computeCostMetrics, onMetricCardClick }: ComputeCostMetricCardsProps) => {
		if (!computeCostMetrics) {
			return null;
		}

		const formatCost = (cost: number) => `$${cost.toFixed(4)}`;
		const formatDuration = (seconds: number) => {
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			const secs = Math.floor(seconds % 60);

			if (hours > 0) {
				return `${hours}h ${minutes}m`;
			}
			if (minutes > 0) {
				return `${minutes}m ${secs}s`;
			}
			return `${secs}s`;
		};

		return (
			<Grid py="lg" w="100%" gutter="md">
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="Total Cost"
						value={formatCost(computeCostMetrics.total_cost)}
						onClick={useCallback(
							() => onMetricCardClick?.('total_cost'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="Total Requests"
						value={computeCostMetrics.total_requests}
						onClick={useCallback(
							() => onMetricCardClick?.('total_requests'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="vCPU Cost"
						value={formatCost(computeCostMetrics.vcpu_cost)}
						onClick={useCallback(
							() => onMetricCardClick?.('vcpu_cost'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="Memory Cost"
						value={formatCost(computeCostMetrics.memory_cost)}
						onClick={useCallback(
							() => onMetricCardClick?.('memory_cost'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="Avg Duration"
						value={formatDuration(computeCostMetrics.avg_duration_seconds)}
						onClick={useCallback(
							() => onMetricCardClick?.('avg_duration'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="Success Rate"
						value={`${computeCostMetrics.success_rate.toFixed(1)}%`}
						onClick={useCallback(
							() => onMetricCardClick?.('success_rate'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="Unique Jobs"
						value={computeCostMetrics.run_info.unique_jobs}
						onClick={useCallback(
							() => onMetricCardClick?.('unique_jobs'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<MetricsOverviewCard
						title="Avg vCPU"
						value={computeCostMetrics.avg_vcpu.toFixed(2)}
						onClick={useCallback(
							() => onMetricCardClick?.('avg_vcpu'),
							[onMetricCardClick]
						)}
					/>
				</Grid.Col>
			</Grid>
		);
	}
);

ComputeCostMetricCards.displayName = 'ComputeCostMetricCards';
